// 提现模块状态管理

import { createWithEqualityFn } from "zustand/traditional";
import { subscribeWithSelector } from "zustand/middleware";
import { getWithdrawConfig } from "../../../api/pay";
import { WithdrawState, WithdrawActions, WithdrawConfig } from "../types";

// 初始状态
const initialState: WithdrawState = {
  withdrawConfig: null,
  withdrawConfigLoading: false,
  withdrawConfigError: null,
  cache: new Map(),
};

// 创建提现store
export const useWithdrawStore = createWithEqualityFn<
  WithdrawState & WithdrawActions
>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    setWithdrawConfig: config => set({ withdrawConfig: config }),
    setWithdrawConfigLoading: loading =>
      set({ withdrawConfigLoading: loading }),
    setWithdrawConfigError: error => set({ withdrawConfigError: error }),

    /**
     * 获取提现配置（每次都重新请求）
     * 确保每次打开页面都获取最新的提现配置
     */
    getWithdrawConfig: async (params?: {
      currencyId?: string;
      countryId?: string;
    }) => {
      const state = get();

      // 如果正在加载，等待加载完成
      if (state.withdrawConfigLoading) {
        return new Promise<WithdrawConfig | null>(resolve => {
          const unsubscribe = useWithdrawStore.subscribe(
            state => state.withdrawConfigLoading,
            loading => {
              if (!loading) {
                unsubscribe();
                resolve(get().withdrawConfig);
              }
            }
          );
        });
      }

      // 开始加载
      set({ withdrawConfigLoading: true, withdrawConfigError: null });

      try {
        const res = await getWithdrawConfig(params);

        if (res.status === 200 && res.data?.model) {
          const config = res.data.model;
          set({
            withdrawConfig: config,
            withdrawConfigLoading: false,
            withdrawConfigError: null,
          });
          return config;
        } else {
          const error = "Failed to load withdraw config";
          set({
            withdrawConfig: null,
            withdrawConfigLoading: false,
            withdrawConfigError: error,
          });
          console.error(error);
          return null;
        }
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Failed to load withdraw config";
        set({
          withdrawConfig: null,
          withdrawConfigLoading: false,
          withdrawConfigError: errorMessage,
        });
        console.error("Failed to load withdraw config:", error);
        return null;
      }
    },

    clearWithdrawConfig: () => {
      set({
        withdrawConfig: null,
        withdrawConfigLoading: false,
        withdrawConfigError: null,
      });
    },

    // BaseModuleActions 实现
    setCache: (key, data, ttl) => {
      const cache = new Map(get().cache);
      cache.set(key, {
        data,
        timestamp: Date.now(),
        ttl: ttl !== undefined ? ttl : 0,
      });
      set({ cache });
    },

    getCache: key => {
      const cache = get().cache;
      const item = cache.get(key);
      if (!item) return null;
      if (item.ttl && Date.now() - item.timestamp > item.ttl) {
        cache.delete(key);
        return null;
      }
      return item.data;
    },

    clearCache: pattern => {
      const cache = new Map(get().cache);
      if (pattern) {
        const regex = new RegExp(pattern);
        for (const key of cache.keys()) {
          if (regex.test(key)) {
            cache.delete(key);
          }
        }
      } else {
        cache.clear();
      }
      set({ cache });
    },

    hasCache: key => {
      return get().cache.has(key);
    },

    getCacheKeys: () => {
      return Array.from(get().cache.keys());
    },

    getCacheSize: () => {
      return get().cache.size;
    },

    cacheFirst: async (key, apiCall, ttl) => {
      const cached = get().getCache(key);
      if (cached) return cached;

      const data = await apiCall();
      get().setCache(key, data, ttl);
      return data;
    },
  }))
);

// 提现配置 hook（懒加载）
export const useWithdrawConfig = () => {
  const {
    withdrawConfig,
    symbol,
    withdrawConfigLoading,
    withdrawConfigError,
    clearWithdrawConfig,
  } = useWithdrawStore(state => ({
    withdrawConfig: state.withdrawConfig,
    symbol: state.withdrawConfig?.symbol,
    withdrawConfigLoading: state.withdrawConfigLoading,
    withdrawConfigError: state.withdrawConfigError,
    clearWithdrawConfig: state.clearWithdrawConfig,
  }));

  const getWithdrawConfig = useWithdrawStore(state => state.getWithdrawConfig);

  return {
    withdrawConfig,
    loading: withdrawConfigLoading,
    error: withdrawConfigError,
    getWithdrawConfig,
    symbol,
    clearWithdrawConfig,
  };
};
